import type { Theme } from "@mui/material";
import { alpha, useTheme } from "@mui/material";
import { darken } from "@mui/system";
import { createSxStyles } from "../createSxStyles";
import type { IButtonProps } from "./Button";

export const useButtonStyles = (props: IButtonProps) => {
    const theme = useTheme();
    return createSxStyles({
        button: {
            ...getButtonStyles(props, theme),
        },
        buttonProgress: {
            width: 65,
            position: "absolute",
            top: "50%",
            left: "50%",
            marginTop: "-12px",
            marginLeft: "-12px",
            color: getButtonStyles(props, theme).color,
        },
    });
};

function getButtonStyles(props: IButtonProps, theme: Theme) {
    let backgroundColor;
    let textColor;
    let borderColor;
    let secondaryHover: string;
    let hoverBackgroundColor: string | undefined;
    let hoverBorderColor: string | undefined;
    let hoverTextColor: string | undefined;
    let disabledBackgroundColor: string | undefined;
    let disabledBorderColor: string | undefined;
    let disabledTextColor: string | undefined;
    switch (props.buttonType) {
        case "primary":
            backgroundColor = theme.palette.common.white;
            textColor = theme.palette.text.secondary;
            borderColor = "#D3D3D3";
            hoverBackgroundColor = theme.palette.primary.main;
            hoverBorderColor = theme.palette.primary.main;
            hoverTextColor = theme.palette.common.white;
            disabledBackgroundColor = "#F0F0F0";
            disabledBorderColor = "#E0E0E0";
            disabledTextColor = "#B0B0B0";
            break;
        case "secondary":
            backgroundColor = theme.palette.common.white;
            textColor = theme.palette.primary.main;
            borderColor = theme.palette.primary.main;
            secondaryHover = theme.palette.primary.main;
            break;
        case "success":
            backgroundColor = theme.palette.secondary.main;
            borderColor = theme.palette.secondary.main;
            textColor = theme.palette.common.white;
            break;
        case "danger":
            backgroundColor = theme.palette.error.main;
            borderColor = theme.palette.error.main;
            textColor = theme.palette.common.white;
            break;
        case "linkButton":
            backgroundColor = "initial";
            borderColor = "transparent";
            textColor = theme.palette.primary.main;
            break;
        case "green":
            backgroundColor = theme.palette.secondary.main;
            borderColor = theme.palette.secondary.main;
            textColor = theme.palette.common.white;
            break;
    }

    return {
        width: props.fullWidth ? "100%" : props.btnLength ? props.btnLength : "auto",
        height: "fit-content",
        backgroundColor: backgroundColor,
        color: textColor,
        borderColor: props.isOnlyIcon ? "unset" : borderColor,
        border: props.isOnlyIcon ? "unset" : `2px solid ${borderColor}`,
        boxShadow: props.buttonType === "linkButton" ? "none" : "inherit",
        padding: "5px 16px",
        "&:hover": {
            backgroundColor:
                hoverBackgroundColor ??
                (props.buttonType === "secondary"
                    ? alpha(secondaryHover!, 0.3)
                    : props.buttonType === "linkButton"
                    ? undefined
                    : darken(backgroundColor!, 0.2)),
            borderColor:
                hoverBorderColor ??
                (props.buttonType === "secondary" ? alpha(secondaryHover!, 0.3) : props.buttonType === "linkButton" ? "none" : darken(borderColor!, 0.2)),
            color: hoverTextColor,
            boxShadow: "inherit",
            textDecoration: props.buttonType === "linkButton" ? "underline" : "",
            padding: "5px 16px",
            borderWidth: props.isOnlyIcon ? "unset" : `2px`,
        },
        "&.MuiButton-contained.Mui-disabled": {
            backgroundColor: disabledBackgroundColor ?? (props.buttonType === "linkButton" ? "initial" : alpha(backgroundColor!, 0.5)),
            borderColor: disabledBorderColor ?? (props.buttonType === "linkButton" ? "transparent" : alpha(backgroundColor!, 0.1)),
        },
        "&.MuiButton-root.Mui-disabled": {
            color: disabledTextColor ?? alpha(textColor!, 0.5),
            borderWidth: "2px",
        },
        "& .arrow-down svg": {
            fill: textColor,
        },
    };
}
